<template>
  <div class="card">
    <h3 class="card-title">{{ $t('customColors.title') }}</h3>

    <!-- 左侧悬浮 删除历史（桌面端） -->
    <div v-if="deletedColors.length" class="deleted-floating" :aria-label="$t('customColors.deleteHistory')">
      <div class="deleted-floating-header">
        <strong>{{ $t('customColors.deleteHistory') }}</strong>
        <span class="count">{{ deletedColors.length }}</span>
        <button class="btn-link" @click="clearHistory" :title="$t('customColors.clearHistory')">{{
          $t('customColors.clearHistory') }}</button>
      </div>
      <div class="deleted-floating-list">
        <div v-for="c in deletedColors" :key="'del-float-' + c.id" class="deleted-floating-item"
          :title="c.name || ($t('customColors.colorPrefix') + c.id)">
          <div class="color-swatch" :style="{ backgroundColor: c.hex }"></div>
          <div class="meta">
            <div class="name">{{ c.name || ($t('customColors.colorPrefix') + c.id) }}</div>
            <div class="hex">{{ c.hex.toUpperCase() }}</div>
          </div>
          <button class="btn-secondary small" @click="restoreColor(c.id)">{{ $t('customColors.restore') }}</button>
        </div>
      </div>
    </div>

    <!-- 画风切换功能 -->
    <div class="art-style-section">
      <h4 class="section-title">{{ $t('customColors.artStyle.title') }}</h4>
      <div class="style-selector">
        <label for="artStyle">{{ $t('customColors.artStyle.select') }}</label>
        <select id="artStyle" v-model="selectedArtStyle" @change="onArtStyleChange" class="style-select">
          <option value="">{{ $t('customColors.artStyle.none') }}</option>
          <option v-for="style in artStyles" :key="style.id" :value="style.id">
            {{ style.name }} - {{ style.description }}
          </option>
          <option value="brand-dmc">{{ $t('customColors.artStyle.dmcBrand') }}</option>
        </select>
        <button v-if="selectedArtStyle !== currentArtStyle" @click="applyArtStyle" class="apply-style-btn">
          {{ $t('customColors.artStyle.apply') }}
        </button>
      </div>
      <div v-if="selectedArtStyle !== undefined" class="style-info">
        <p v-if="selectedArtStyle" class="style-description">
          <strong>{{ $t('customColors.artStyle.recommendedAlgorithm') }}</strong>
          <span class="algorithm-tag">{{ getAlgorithmDisplayName(getSelectedStyleAlgorithm()) }}</span>
        </p>
        <p v-if="selectedArtStyle" class="style-description">
          <strong>{{ $t('customColors.artStyle.colorCount') }}</strong> {{ getSelectedStyleColors().length }} {{
            $t('common.colors') }}
        </p>
        <p v-if="!selectedArtStyle" class="style-description">
          <strong>{{ $t('customColors.artStyle.status') }}</strong> <span class="algorithm-tag">{{
            $t('customColors.artStyle.noCustomColors') }}</span>
        </p>
        <p v-if="brandDisclaimer && selectedArtStyle === 'brand-dmc'" class="style-description" style="opacity:.9;">
          {{ brandDisclaimer }}
        </p>
      </div>
    </div>

    <!-- 颜色归并阈值设置 -->
    <div class="color-merge-section">
      <h4 class="color-merge-title">{{ $t('customColors.colorMerge.title') }}</h4>
      <div class="merge-controls">
        <div class="merge-mode">
          <label for="mergeMode">{{ $t('customColors.colorMerge.mode') }}</label>
          <select id="mergeMode" v-model="mergeMode" class="merge-select">
            <option value="rgb">{{ $t('customColors.colorMerge.rgbThreshold') }}</option>
            <option value="cielab">{{ $t('customColors.colorMerge.cielabThreshold') }}</option>
          </select>
        </div>

        <div class="merge-threshold">
          <label for="mergeThreshold">
            {{ mergeMode === 'rgb' ? $t('customColors.colorMerge.rgbThreshold') :
              $t('customColors.colorMerge.cielabThreshold') }}:
            <span class="threshold-value">{{ mergeThreshold }}</span>
          </label>
          <input id="mergeThreshold" type="range" v-model.number="mergeThreshold" :min="mergeMode === 'rgb' ? 0 : 0.1"
            :max="mergeMode === 'rgb' ? 50 : 10" :step="mergeMode === 'rgb' ? 1 : 0.1" class="threshold-slider"
            @input="onThresholdChange" />
          <div class="threshold-info">
            <span v-if="mergeMode === 'rgb'">
              {{ $t('customColors.colorMerge.rgbRecommend') }}
            </span>
            <span v-else>
              {{ $t('customColors.colorMerge.cielabRecommend') }}
            </span>
          </div>
        </div>

        <div class="merge-base-color">
          <label for="baseHex">{{ $t('customColors.colorMerge.baseColor') }}(#RRGGBB):</label>
          <div class="hex-input-wrapper">
            <span class="hex-prefix">#</span>
            <input id="baseHex" type="text" v-model="baseHexNoHash" maxlength="6" placeholder="FFFFFF"
              class="hex-input" />
          </div>
          <small class="hint-text">{{ $t('customColors.colorMerge.baseColor') }}</small>
        </div>

        <div class="merge-actions">
          <button @click="applyColorMerge" class="merge-btn" :disabled="isMerging">
            {{ isMerging ? $t('customColors.colorMerge.merging') : $t('customColors.colorMerge.applyMerge') }}
          </button>
          <button @click="resetMergeSettings" class="reset-btn">{{ $t('customColors.colorMerge.resetSettings')
            }}</button>
        </div>

        <div v-if="mergeResult" class="merge-result">
          <p class="result-info">
            <strong>{{ $t('customColors.colorMerge.mergeResult') }}</strong>
            {{ $t('customColors.colorMerge.fromColors') }} {{ mergeResult.originalCount }} {{ $t('common.colors') }} {{
              $t('customColors.colorMerge.toColors') }} {{ mergeResult.mergedCount }} {{ $t('common.colors') }}
            <span class="reduction">({{ $t('customColors.colorMerge.reduction') }} {{ mergeResult.reduction }}%)</span>
          </p>
        </div>
      </div>
    </div>

    <div class="color-form">
      <!-- 输入方式切换 -->
      <div class="input-mode-switch">
        <label class="switch-label">
          <input type="radio" v-model="inputMode" value="rgb" name="inputMode" />
          <span class="switch-text">{{ $t('customColors.colorMerge.rgbInput') }}</span>
        </label>
        <label class="switch-label">
          <input type="radio" v-model="inputMode" value="hex" name="inputMode" />
          <span class="switch-text">{{ $t('customColors.colorMerge.hexInput') }}</span>
        </label>
      </div>

      <!-- RGB输入模式 -->
      <div v-if="inputMode === 'rgb'" class="color-inputs">
        <div class="color-input-group">
          <label for="colorR">R:</label>
          <input id="colorR" type="number" v-model.number="newColor.r" min="0" max="255" placeholder="0-255"
            @input="updateFromRGB" />
        </div>

        <div class="color-input-group">
          <label for="colorG">G:</label>
          <input id="colorG" type="number" v-model.number="newColor.g" min="0" max="255" placeholder="0-255"
            @input="updateFromRGB" />
        </div>

        <div class="color-input-group">
          <label for="colorB">B:</label>
          <input id="colorB" type="number" v-model.number="newColor.b" min="0" max="255" placeholder="0-255"
            @input="updateFromRGB" />
        </div>
      </div>

      <!-- 16进制输入模式 -->
      <div v-else class="color-inputs">
        <div class="hex-input-group">
          <label for="colorHex">{{ $t('customColors.colorMerge.hexLabel') }}</label>
          <div class="hex-input-wrapper">
            <span class="hex-prefix">#</span>
            <input id="colorHex" type="text" v-model="hexInput" placeholder="000000" maxlength="6"
              pattern="[0-9A-Fa-f]{6}" @input="updateFromHexWithHash" class="hex-input"
              :class="{ 'input-valid': isValidHexInput }" />
          </div>
          <div v-if="hexInput" class="hex-input-hint">
            <span v-if="hexInput.replace('#', '').length < 6" class="hint-text">
              {{ $t('customColors.colorMerge.hexInputHint') }} {{ hexInput.replace('#', '').length }}{{
                $t('customColors.colorMerge.hexInputHint2') }}
            </span>
            <span v-else class="hint-text success">
              {{ $t('customColors.colorMerge.hexComplete') }}
            </span>
          </div>
        </div>
      </div>

      <div class="color-preview">
        <div class="color-swatch" :style="{ backgroundColor: `rgb(${newColor.r}, ${newColor.g}, ${newColor.b})` }">
        </div>
        <div class="color-values-display">
          <span class="hex-value">{{ newColorHex }}</span>
          <span class="rgb-value">RGB({{ newColor.r }}, {{ newColor.g }}, {{ newColor.b }})</span>
        </div>
        <input type="color" v-model="newColorPickerHex" :aria-label="$t('customColors.colorMerge.selectColor')" />
      </div>

      <div class="color-form-actions">
        <input v-model="newColor.name" type="text" :placeholder="$t('customColors.colorMerge.colorNamePlaceholder')"
          class="color-name-input" />
        <button class="btn-success" @click="addColor" :disabled="!isValidColorValue">
          {{ $t('customColors.colorMerge.addColor') }}
        </button>
      </div>
    </div>


    <div v-if="customColors.length > 0" class="color-list">
      <h4>{{ $t('customColors.colorMerge.addedColors') }} ({{ customColors.length }})</h4>
      <div class="color-items">
        <div v-for="color in customColors" :key="color.id" class="color-item">
          <div class="color-swatch" :style="{ backgroundColor: color.hex }"></div>
          <div class="color-info">
            <div class="color-values">
              <span class="rgb-values">RGB({{ color.r }}, {{ color.g }}, {{ color.b }})</span>
              <span class="hex-value">{{ color.hex }}</span>
            </div>
            <div class="color-name">{{ color.name }}</div>
          </div>
          <button class="btn-secondary edit-btn" @click="openEditColor(color)"
            :title="$t('customColors.colorMerge.editColor')">
            ✎
          </button>
          <button class="btn-danger remove-btn" @click="removeColor(color.id)"
            :title="$t('customColors.colorMerge.deleteColor')">
            ×
          </button>
        </div>
      </div>
    </div>

    <div v-else class="no-colors">
      <p>{{ $t('customColors.colorMerge.noCustomColorsYet') }}</p>
      <p class="text-muted">{{ $t('customColors.colorMerge.addColorsHint') }}</p>
    </div>
    <!-- 导出导入功能 -->
    <div class="export-import-section">
      <h4>{{ $t('customColors.colorMerge.exportImport') }}</h4>

      <!-- 导出功能 -->
      <div class="export-section">
        <h5>{{ $t('customColors.colorMerge.exportColors') }}</h5>
        <p class="section-desc">{{ $t('customColors.colorMerge.exportDesc') }}</p>
        <div class="export-actions">
          <button class="btn-primary" @click="exportColors" :disabled="customColors.length === 0">
            {{ $t('customColors.colorMerge.exportColors') }}
          </button>
          <div v-if="exportedString" class="export-result">
            <label for="exportString">{{ $t('customColors.colorMerge.exportString') }}</label>
            <div class="export-string-wrapper">
              <input id="exportString" type="text" :value="exportedString" readonly class="export-string-input" />
              <button class="btn-secondary copy-btn" @click="copyToClipboard"
                :title="$t('customColors.colorMerge.copyToClipboard')">
                📋
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 导入功能 -->
      <div class="import-section">
        <h5>{{ $t('customColors.colorMerge.importColors') }}</h5>
        <p class="section-desc">{{ $t('customColors.colorMerge.importDesc') }}</p>
        <div class="import-actions">
          <div class="import-input-wrapper">
            <input v-model="importString" type="text" :placeholder="$t('customColors.colorMerge.pasteString')"
              class="import-string-input" />
            <button class="btn-success" @click="importColors" :disabled="!importString.trim()">
              {{ $t('customColors.colorMerge.importColor') }}
            </button>
          </div>
          <div v-if="importResult" class="import-result" :class="importResult.type">
            {{ importResult.message }}
          </div>
        </div>
      </div>

      <!-- 自动生成调色板 -->
      <div class="export-import-section">
        <h4>{{ $t('customColors.colorMerge.generatePalette') }}</h4>
        <p class="section-desc">{{ $t('customColors.colorMerge.generatePaletteDesc') }}</p>
        <div class="export-actions">
          <div class="import-input-wrapper">
            <label style="min-width: 110px;">{{ $t('customColors.colorMerge.targetColorCount') }}</label>
            <input type="range" v-model.number="targetPaletteSize" min="4" max="128" step="1" style="width:110px;" />
            <span style="min-width: 40px; text-align:center;">{{ targetPaletteSize }}</span>
            <button class="btn-primary" @click="generateAutoPalette"
              :disabled="!canGeneratePalette || isGeneratingPalette">
              {{ isGeneratingPalette ? $t('customColors.colorMerge.generating') :
                $t('customColors.colorMerge.generatePaletteBtn') }}
            </button>
          </div>
          <div v-if="paletteResultMsg" class="import-result" :class="paletteResultType">{{ paletteResultMsg }}</div>
        </div>
      </div>

      <!-- 批量导入功能 -->
      <div class="batch-import-section">
        <h5>{{ $t('customColors.colorMerge.batchImport') }}</h5>
        <p class="section-desc">{{ $t('customColors.colorMerge.batchImportDesc') }}
        </p>
        <div class="batch-import-actions">
          <div class="batch-import-input-wrapper">
            <textarea v-model="batchImportString" :placeholder="$t('customColors.colorMerge.batchImportPlaceholder')"
              class="batch-import-textarea" rows="5"></textarea>
            <button class="btn-warning" @click="batchImportColors" :disabled="!batchImportString.trim()">
              {{ $t('customColors.colorMerge.batchImportBtn') }}
            </button>
          </div>
          <div v-if="batchImportResult" class="import-result" :class="batchImportResult.type">
            {{ batchImportResult.message }}
          </div>
        </div>
      </div>
    </div>
    <!-- 编辑颜色弹窗 -->
    <div v-if="isEditOpen" class="modal-overlay" @click.self="closeEdit">
      <div class="modal">
        <h4 class="modal-title">{{ $t('customColors.editColorModal') }}</h4>
        <div class="modal-body">
          <div class="edit-preview">
            <div class="color-swatch" :style="{ backgroundColor: editHex }"></div>
            <div class="color-values-display">
              <span class="hex-value">{{ editHex }}</span>
              <span class="rgb-value">RGB({{ editColor.r }}, {{ editColor.g }}, {{ editColor.b }})</span>
            </div>
            <input type="color" v-model="editHexColorPicker" :aria-label="$t('customColors.colorMerge.selectColor')" />
          </div>
          <div class="color-inputs">
            <div class="color-input-group">
              <label>R:</label>
              <input type="number" v-model.number="editColor.r" min="0" max="255" @input="syncEditHexFromRgb" />
            </div>
            <div class="color-input-group">
              <label>G:</label>
              <input type="number" v-model.number="editColor.g" min="0" max="255" @input="syncEditHexFromRgb" />
            </div>
            <div class="color-input-group">
              <label>B:</label>
              <input type="number" v-model.number="editColor.b" min="0" max="255" @input="syncEditHexFromRgb" />
            </div>
            <div class="color-input-group">
              <label>HEX:</label>
              <div class="hex-input-wrapper">
                <span class="hex-prefix">#</span>
                <input type="text" v-model="editHexNoHash" maxlength="6" @input="syncEditRgbFromHex"
                  class="hex-input" />
              </div>
            </div>
          </div>
          <div class="color-input-group" style="margin-top: .5rem;">
            <label>{{ $t('customColors.colorMerge.nameLabel') }}</label>
            <input type="text" v-model="editName" :placeholder="$t('customColors.colorMerge.colorNamePlaceholder2')" />
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="closeEdit">{{ $t('customColors.colorMerge.cancel') }}</button>
          <button class="btn-success" @click="saveEdit" :disabled="!isEditValid">{{ $t('customColors.colorMerge.save')
            }}</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColorStore, type ColorPoint } from '../stores/colorStore'
import { rgbToHex, hexToRgb, isValidColor, generatePaletteFromExtractedColors } from '../utils/colorUtils'
import { usePreviewStore } from '../stores/previewStore'

const { t } = useI18n()

const colorStore = useColorStore()

// 画风相关状态
const selectedArtStyle = ref('')
const artStyles = computed(() => colorStore.getArtStyles())
const brandDisclaimer = computed(() => colorStore.brandDisclaimer)
const currentArtStyle = computed(() => colorStore.getCurrentArtStyle())

const inputMode = ref<'rgb' | 'hex'>('rgb')
const hexInput = ref('')

const newColor = ref({
  r: 0,
  g: 0,
  b: 0,
  name: ''
})

// 导出导入相关状态
const exportedString = ref('')
const importString = ref('')

// 颜色归并相关状态
const mergeMode = computed({
  get: () => colorStore.mergeMode,
  set: (val: 'rgb' | 'cielab') => colorStore.setMergeMode(val)
})
const mergeThreshold = computed({
  get: () => colorStore.mergeThreshold,
  set: (val: number) => colorStore.setMergeThreshold(val)
})
const isMerging = ref(false)
const mergeResult = ref<{
  originalCount: number
  mergedCount: number
  reduction: number
} | null>(null)
const importResult = ref<{ type: 'success' | 'error'; message: string } | null>(null)

// 批量导入相关状态
const batchImportString = ref('')
const batchImportResult = ref<{ type: 'success' | 'error'; message: string } | null>(null)

const customColors = computed(() => colorStore.customColors)
const deletedColors = computed(() => colorStore.getDeletedColors())

// 自动调色板状态
const targetPaletteSize = ref(32)
const isGeneratingPalette = ref(false)
const paletteResultMsg = ref('')
const paletteResultType = ref<'success' | 'error'>('success')
const canGeneratePalette = computed(() => !!colorStore.extractedColors)

// 底色输入（仅在应用时写入store，不做实时监听）
const baseHexNoHash = ref(colorStore.baseFabricColor?.hex.replace('#', ''))

async function generateAutoPalette() {
  if (!colorStore.extractedColors) {
    paletteResultType.value = 'error'
    paletteResultMsg.value = t('customColors.colorMerge.pleaseUploadFirst')
    return
  }
  isGeneratingPalette.value = true
  paletteResultMsg.value = ''
  try {
    const palette = await generatePaletteFromExtractedColors(
      colorStore.extractedColors.pixels,
      Math.max(4, Math.min(128, targetPaletteSize.value)),
      { sampleRate: 0.3 }
    )
    // 替换自定义颜色
    colorStore.clearCustomColors()
    for (const c of palette) {
      colorStore.addCustomColor({ r: c.r, g: c.g, b: c.b, hex: c.hex, name: c.name })
    }
    // 切换到 CIEDE2000 匹配
    colorStore.setColorMatchingAlgorithm('ciede2000')
    paletteResultType.value = 'success'
    paletteResultMsg.value = `${t('customColors.colorMerge.generatedColors')} ${palette.length} ${t('customColors.colorMerge.representativeColors')}`
  } catch (e) {
    console.error(e)
    paletteResultType.value = 'error'
    paletteResultMsg.value = t('customColors.colorMerge.generateFailed')
  } finally {
    isGeneratingPalette.value = false
  }
}

// 编辑颜色弹窗状态
const isEditOpen = ref(false)
const editingId = ref<number | null>(null)
const editColor = ref({ r: 0, g: 0, b: 0 })
const editHex = computed(() => rgbToHex(editColor.value.r, editColor.value.g, editColor.value.b))
const editHexColorPicker = computed({
  get: () => editHex.value,
  set: (val: string) => {
    const rgb = hexToRgb(val)
    if (rgb) {
      editColor.value = { r: rgb.r, g: rgb.g, b: rgb.b }
    }
  }
})
const editHexNoHash = computed({
  get: () => editHex.value.replace('#', ''),
  set: (v: string) => {
    const clean = v.replace('#', '')
    if (/^[0-9A-Fa-f]{0,6}$/.test(clean)) {
      const padded = clean.padEnd(6, '0')
      const rgb = hexToRgb('#' + padded)
      if (rgb) {
        editColor.value = { r: rgb.r, g: rgb.g, b: rgb.b }
      }
    }
  }
})
const editName = ref('')
const isEditValid = computed(() => isValidColor(editColor.value.r, editColor.value.g, editColor.value.b))

function openEditColor(color: { id: number; r: number; g: number; b: number; hex: string; name?: string }) {
  editingId.value = color.id
  editColor.value = { r: color.r, g: color.g, b: color.b }
  editName.value = color.name || ''
  isEditOpen.value = true
}

function closeEdit() {
  isEditOpen.value = false
  editingId.value = null
}

function syncEditHexFromRgb() {
  // computed editHex will reflect automatically
}

function syncEditRgbFromHex() {
  // handled in editHexNoHash setter
}

function saveEdit() {
  if (!isEditValid.value || editingId.value == null) return
  const hex = editHex.value
  colorStore.updateCustomColor(editingId.value, {
    r: editColor.value.r,
    g: editColor.value.g,
    b: editColor.value.b,
    hex,
    name: editName.value.trim() || undefined
  })
  closeEdit()
}

const newColorHex = computed(() => {
  return rgbToHex(newColor.value.r, newColor.value.g, newColor.value.b)
})

const newColorPickerHex = computed({
  get: () => newColorHex.value,
  set: (val: string) => {
    const rgb = hexToRgb(val)
    if (rgb) {
      newColor.value.r = rgb.r
      newColor.value.g = rgb.g
      newColor.value.b = rgb.b
      hexInput.value = val.replace('#', '')
    }
  }
})

const isValidColorValue = computed(() => {
  return isValidColor(newColor.value.r, newColor.value.g, newColor.value.b)
})

const isValidHexInput = computed(() => {
  const hex = hexInput.value.trim()
  // 移除可能存在的#前缀，只保留16进制字符
  const cleanHex = hex.replace('#', '')
  return cleanHex.length > 0 && /^[0-9A-Fa-f]*$/.test(cleanHex)
})

// 从RGB值更新16进制输入
function updateFromRGB() {
  hexInput.value = newColorHex.value.replace('#', '')
}

// 从16进制输入更新RGB值
function updateFromHex() {
  const hex = hexInput.value.trim()

  // 移除可能存在的#前缀，只保留16进制字符
  const cleanHex = hex.replace('#', '')

  // 如果输入长度不足6位，用0补齐
  if (cleanHex.length > 0 && cleanHex.length <= 6 && /^[0-9A-Fa-f]*$/.test(cleanHex)) {
    const paddedHex = cleanHex.padEnd(6, '0')
    const rgb = hexToRgb(paddedHex)
    if (rgb) {
      newColor.value.r = rgb.r
      newColor.value.g = rgb.g
      newColor.value.b = rgb.b
    }
  }
}

// 从16进制输入更新RGB值（带#前缀）
function updateFromHexWithHash() {
  const hex = hexInput.value.trim()

  // 移除可能存在的#前缀，只保留16进制字符
  const cleanHex = hex.replace('#', '')

  // 如果输入长度不足6位，用0补齐
  if (cleanHex.length > 0 && cleanHex.length <= 6 && /^[0-9A-Fa-f]*$/.test(cleanHex)) {
    const paddedHex = cleanHex.padEnd(6, '0')
    const rgb = hexToRgb('#' + paddedHex)
    if (rgb) {
      newColor.value.r = rgb.r
      newColor.value.g = rgb.g
      newColor.value.b = rgb.b
    }
  }
}

// 监听输入模式变化，同步显示
watch(inputMode, (newMode) => {
  if (newMode === 'hex') {
    hexInput.value = newColorHex.value.replace('#', '')
  }
})

function addColor() {
  if (isValidColorValue.value) {
    // 如果用户没有输入名字，自动生成序号
    const colorName = newColor.value.name.trim() || `${t('customColors.colorMerge.colorPrefix')}${customColors.value.length + 1}`

    colorStore.addCustomColor({
      r: newColor.value.r,
      g: newColor.value.g,
      b: newColor.value.b,
      hex: newColorHex.value,
      name: colorName
    })

    // 重置表单
    newColor.value = { r: 0, g: 0, b: 0, name: '' }
    hexInput.value = ''
  }
}

// 画风相关方法
function onArtStyleChange() {
  // 当画风改变时，可以在这里添加一些逻辑
}

async function applyArtStyle() {
  if (selectedArtStyle.value) {
    try {
      if (selectedArtStyle.value === 'brand-dmc') {
        // 应用品牌色卡
        colorStore.applyBrandPalette('dmc')
      } else {
        colorStore.applyArtStyle(selectedArtStyle.value)
      }
      // 显示成功消息
      if (importResult.value) {
        importResult.value = {
          type: 'success',
          message: `已应用${getSelectedStyleName() || (selectedArtStyle.value === 'brand-dmc' ? 'DMC 色卡' : '')}！`
        }
      }
    } catch (error) {
      console.error('应用画风失败:', error)
      if (importResult.value) {
        importResult.value = {
          type: 'error',
          message: '应用画风失败，请重试'
        }
      }
    }
  } else {
    // 选择空值时，清空自定义颜色（无画风）
    colorStore.clearCustomColors()
    // 更新当前画风状态为空（无画风）
    colorStore.currentArtStyle = ''
    colorStore.currentBrand = ''
    colorStore.brandDisclaimer = ''
    if (importResult.value) {
      importResult.value = {
        type: 'success',
        message: '已清空自定义颜色（无画风）'
      }
    }
  }
}

function getSelectedStyleName(): string {
  if (selectedArtStyle.value === 'brand-dmc') return 'DMC 色卡'
  const style = artStyles.value.find(s => s.id === selectedArtStyle.value)
  return style ? style.name : ''
}

function getSelectedStyleAlgorithm(): 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000' {
  if (selectedArtStyle.value === 'brand-dmc') return 'ciede2000'
  const style = artStyles.value.find(s => s.id === selectedArtStyle.value)
  return style ? style.recommendedAlgorithm : 'rgb'
}

function getSelectedStyleColors() {
  if (!selectedArtStyle.value) {
    return []
  }
  if (selectedArtStyle.value === 'brand-dmc') {
    // 品牌色卡颜色数量在应用后体现在 customColors 中，这里仅返回当前列表
    return customColors.value
  }
  const style = artStyles.value.find(s => s.id === selectedArtStyle.value)
  return style ? style.colors : []
}

function getAlgorithmDisplayName(algorithm: 'rgb' | 'lab' | 'weighted-rgb' | 'hsv' | 'ciede2000'): string {
  switch (algorithm) {
    case 'rgb':
      return 'RGB'
    case 'lab':
      return 'CIELAB'
    case 'weighted-rgb':
      return '加权RGB'
    case 'hsv':
      return 'HSV'
    case 'ciede2000':
      return 'CIEDE2000'
    default:
      return 'RGB'
  }
}

function removeColor(id: number) {
  colorStore.removeCustomColor(id)
}

function restoreColor(id: number) {
  colorStore.restoreDeletedColor(id)
}

function clearHistory() {
  if (confirm('确定清空删除历史吗？该操作不可撤销。')) {
    colorStore.clearDeletedHistory()
  }
}

// 颜色归并相关方法
function onThresholdChange() {
  // 阈值改变时的处理逻辑
  mergeResult.value = null
}

function resetMergeSettings() {
  colorStore.setMergeMode('rgb')
  colorStore.setMergeThreshold(2)
  mergeResult.value = null
}

async function applyColorMerge() {
  if (!colorStore.extractedColors || colorStore.extractedColors.pixels.length === 0) {
    alert('请先上传图片并提取颜色')
    return
  }

  isMerging.value = true
  mergeResult.value = null

  try {
    // 应用底色设置（仅在点击时）
    const clean = baseHexNoHash.value?.trim() || ''
    if (clean && /^[0-9A-Fa-f]{6}$/.test(clean)) {
      const hex = '#' + clean.toUpperCase()
      const rgb = hexToRgb(hex)
      if (rgb) {
        colorStore.setBaseFabricColor({ ...rgb, hex })
      }
    } else {
      colorStore.setBaseFabricColor(null)
    }
    // 直接触发相近色预览图更新，颜色归并会在 generatePreviewImage 中自动执行
    const previewStore = usePreviewStore()
    await previewStore.renderCustomPreviewOnly()

    // 从 previewStore 获取归并结果
    mergeResult.value = previewStore.mergeResult

  } catch (error) {
    console.error('颜色归并失败:', error)
    alert('颜色归并失败，请重试')
  } finally {
    isMerging.value = false
  }
}

// 导出颜色配置为密文字符串
function exportColors() {
  try {
    const colorData = customColors.value.map(color => ({
      r: color.r,
      g: color.g,
      b: color.b,
      name: color.name
    }))

    // 将颜色数据转换为JSON字符串，然后进行Base64编码
    const jsonString = JSON.stringify(colorData)

    // 使用 encodeURIComponent 处理中文字符，然后进行 Base64 编码
    const encodedString = btoa(encodeURIComponent(jsonString))

    exportedString.value = encodedString
    importResult.value = null // 清除之前的导入结果
  } catch (error) {
    console.error('导出颜色配置失败:', error)
    importResult.value = {
      type: 'error',
      message: '导出失败，请重试'
    }
  }
}

// 导入颜色配置
function importColors() {
  try {
    if (!importString.value.trim()) {
      importResult.value = {
        type: 'error',
        message: '请输入要导入的密文字符串'
      }
      return
    }

    // 解码Base64字符串，然后解码URI组件
    const decodedString = decodeURIComponent(atob(importString.value.trim()))
    const colorData = JSON.parse(decodedString)

    // 验证数据格式
    if (!Array.isArray(colorData)) {
      throw new Error('无效的颜色数据格式')
    }

    // 验证每个颜色对象
    for (const color of colorData) {
      if (typeof color.r !== 'number' || typeof color.g !== 'number' || typeof color.b !== 'number' || typeof color.name !== 'string') {
        throw new Error('颜色数据格式不正确')
      }

      // 验证RGB值范围
      if (color.r < 0 || color.r > 255 || color.g < 0 || color.g > 255 || color.b < 0 || color.b > 255) {
        throw new Error('RGB值超出有效范围(0-255)')
      }
    }

    // 清空现有颜色并导入新颜色
    colorStore.clearCustomColors()

    for (const color of colorData) {
      colorStore.addCustomColor({
        r: color.r,
        g: color.g,
        b: color.b,
        hex: rgbToHex(color.r, color.g, color.b),
        name: color.name
      })
    }

    // 显示成功消息
    importResult.value = {
      type: 'success',
      message: `成功导入 ${colorData.length} 个颜色`
    }

    // 清空导入输入框
    importString.value = ''

    // 清空导出结果
    exportedString.value = ''

  } catch (error) {
    console.error('导入颜色配置失败:', error)
    importResult.value = {
      type: 'error',
      message: '导入失败：无效的密文字符串或数据格式错误'
    }
  }
}

// 批量导入颜色
function batchImportColors() {
  try {
    if (!batchImportString.value.trim()) {
      batchImportResult.value = {
        type: 'error',
        message: '请输入要批量导入的颜色值'
      }
      return
    }

    const inputText = batchImportString.value.trim()
    let colorData: Array<{ name: string; r: number; g: number; b: number }> = []

    console.log('输入文本：', inputText)
    console.log('是否包含分号：', inputText.includes(';'))
    console.log('分号位置：', inputText.indexOf(';'))

    // 检测输入格式：如果包含分号，按分号分割；否则按逗号分割
    if (inputText.includes(';')) {
      // 格式：用分号分隔的不同颜色行
      const colorLines = inputText.split(';').filter(line => line.trim())

      if (colorLines.length === 0) {
        throw new Error('没有找到有效的颜色数据')
      }

      console.log('检测到分号分隔格式，行数：', colorLines.length)

      for (let i = 0; i < colorLines.length; i++) {
        const line = colorLines[i].trim()
        if (!line) continue

        const parts = line.split(',').map(part => part.trim())
        console.log(`第${i + 1}行：${line}，分割后：`, parts)

        // 智能识别输入格式
        if (parts.length === 1) {
          // 格式：单个16进制颜色值
          const hexValue = parts[0]
          if (hexValue.startsWith('#')) {
            const rgb = hexToRgb(hexValue)
            if (rgb) {
              colorData.push({
                name: `颜色${i + 1}`,
                r: rgb.r,
                g: rgb.g,
                b: rgb.b
              })
            } else {
              throw new Error(`第${i + 1}行无效的16进制颜色值：${hexValue}`)
            }
          } else {
            throw new Error(`第${i + 1}行格式错误：${line}，应为：#RRGGBB 或 颜色名称,R,G,B`)
          }
        } else if (parts.length === 2) {
          // 格式：颜色名称,#RRGGBB
          const [name, hexValue] = parts

          if (!name) {
            throw new Error(`第${i + 1}行颜色名称不能为空：${line}`)
          }

          if (hexValue.startsWith('#')) {
            const rgb = hexToRgb(hexValue)
            if (rgb) {
              colorData.push({ name, r: rgb.r, g: rgb.g, b: rgb.b })
            } else {
              throw new Error(`第${i + 1}行无效的16进制颜色值：${hexValue}`)
            }
          } else {
            throw new Error(`第${i + 1}行格式错误：${line}，应为：颜色名称,#RRGGBB`)
          }
        } else if (parts.length === 4) {
          // 格式：颜色名称,R,G,B
          const [name, rStr, gStr, bStr] = parts

          if (!name) {
            throw new Error(`第${i + 1}行颜色名称不能为空`)
          }

          const r = parseInt(rStr)
          const g = parseInt(gStr)
          const b = parseInt(bStr)

          if (isNaN(r) || isNaN(g) || isNaN(b)) {
            throw new Error(`第${i + 1}行RGB值必须是数字：${line}`)
          }

          if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
            throw new Error(`第${i + 1}行RGB值超出有效范围(0-255)：${line}`)
          }

          colorData.push({ name, r, g, b })
        } else {
          throw new Error(`第${i + 1}行格式错误：${line}，支持格式：1) #RRGGBB 2) 颜色名称,#RRGGBB 3) 颜色名称,R,G,B`)
        }
      }
    } else {
      // 格式：用逗号分隔的16进制颜色值列表
      const hexValues = inputText.split(',').map(part => part.trim()).filter(part => part)

      if (hexValues.length === 0) {
        throw new Error('没有找到有效的颜色数据')
      }

      console.log('检测到纯16进制格式，解析值：', hexValues)

      for (let i = 0; i < hexValues.length; i++) {
        const hexValue = hexValues[i]
        console.log(`处理第${i + 1}个颜色值：`, hexValue)

        if (hexValue.startsWith('#')) {
          const rgb = hexToRgb(hexValue)
          console.log(`16进制值 ${hexValue} 转换为RGB：`, rgb)

          if (rgb) {
            colorData.push({
              name: `颜色${i + 1}`,
              r: rgb.r,
              g: rgb.g,
              b: rgb.b
            })
          } else {
            throw new Error(`第${i + 1}个颜色无效的16进制值：${hexValue}`)
          }
        } else {
          throw new Error(`第${i + 1}个颜色格式错误：${hexValue}，应为：#RRGGBB`)
        }
      }
    }

    if (colorData.length === 0) {
      throw new Error('没有找到有效的颜色数据')
    }

    // 清空现有颜色并导入新颜色
    colorStore.clearCustomColors()

    for (const color of colorData) {
      colorStore.addCustomColor({
        r: color.r,
        g: color.g,
        b: color.b,
        hex: rgbToHex(color.r, color.g, color.b),
        name: color.name
      })
    }

    // 显示成功消息
    batchImportResult.value = {
      type: 'success',
      message: `成功批量导入 ${colorData.length} 个颜色`
    }

    // 清空批量导入输入框
    batchImportString.value = ''

    // 清空其他导入结果
    importResult.value = null
    exportedString.value = ''

  } catch (error) {
    console.error('批量导入颜色失败:', error)
    batchImportResult.value = {
      type: 'error',
      message: `批量导入失败：${error instanceof Error ? error.message : '未知错误'}`
    }
  }
}

// 复制到剪贴板
async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(exportedString.value)
    // 可以添加一个临时的成功提示
    const originalText = importResult.value?.message
    importResult.value = {
      type: 'success',
      message: '已复制到剪贴板！'
    }

    // 3秒后恢复原来的消息
    setTimeout(() => {
      if (importResult.value?.message === '已复制到剪贴板！') {
        importResult.value = originalText ? { type: 'success', message: originalText } : null
      }
    }, 3000)

  } catch (error) {
    console.error('复制失败:', error)
    importResult.value = {
      type: 'error',
      message: '复制失败，请手动复制'
    }
  }
}

// 调试工具：重置颜色ID
function debugReset() {
  colorStore.debugResetIds()
  alert('颜色ID已重置！请刷新页面以应用更改。')
}
</script>

<style scoped>
/* 颜色归并设置样式 */
.color-merge-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.merge-controls {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.merge-mode {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.merge-mode label {
  font-weight: 500;
  min-width: 80px;
}

.merge-select {
  padding: 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: white;
  font-size: 0.9rem;
}

.merge-threshold {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.merge-threshold label {
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.threshold-value {
  color: #007bff;
  font-weight: bold;
  min-width: 30px;
}

.threshold-slider {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #ddd;
  outline: none;
}

.threshold-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
}

.threshold-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: none;
}

.threshold-info {
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
}

.merge-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.merge-btn {
  background: #28a745;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.merge-btn:hover:not(:disabled) {
  background: #218838;
}

.merge-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.reset-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.reset-btn:hover {
  background: #5a6268;
}

.merge-result {
  padding: 0.75rem;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  margin-top: 0.5rem;
}

.result-info {
  margin: 0;
  color: #155724;
  font-size: 0.9rem;
}

.reduction {
  color: #28a745;
  font-weight: bold;
}

.color-form {
  margin-bottom: 1.5rem;
}

.input-mode-switch {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.switch-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.switch-label:hover {
  background-color: #e9ecef;
}

.switch-label input[type="radio"] {
  margin: 0;
}

.switch-text {
  font-weight: 500;
  color: #495057;
}

.color-inputs {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.color-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.color-input-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

.color-input-group input {
  width: 80px;
  text-align: center;
}

.hex-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.hex-input-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
}

.hex-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
}

.hex-prefix {
  background: #f8f9fa;
  padding: 0.5rem 0.75rem;
  border-right: 1px solid #ced4da;
  color: #6c757d;
  font-weight: 500;
  font-family: monospace;
}

.hex-input {
  border: none;
  padding: 0.5rem 0.75rem;
  width: 120px;
  font-family: monospace;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.hex-input:focus {
  outline: none;
  background-color: #fff;
}

.hex-input.input-valid {
  border-color: #28a745;
  background-color: #f8fff9;
}

.hex-input-hint {
  margin-top: 0.5rem;
  font-size: 0.75rem;
}

.hint-text {
  color: #6c757d;
  font-style: italic;
}

.hint-text.success {
  color: #28a745;
  font-weight: 500;
}

.color-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.color-values-display {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.rgb-value {
  font-size: 0.875rem;
  color: #6c757d;
  font-family: monospace;
}

.color-swatch {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hex-value {
  font-family: monospace;
  font-weight: 600;
  color: #495057;
}

.color-form-actions {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.color-name-input {
  flex: 1;
  max-width: 200px;
}

.color-list h4 {
  margin-bottom: 1rem;
  color: #495057;
}

.color-items {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 360px;
  /* 限制高度，出现滚动条 */
  overflow-y: auto;
  padding-right: 6px;
  /* 为滚动条预留空间，避免抖动 */
}

.color-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.color-info {
  flex: 1;
}

.color-values {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.rgb-values {
  font-size: 0.875rem;
  color: #6c757d;
}

.color-name {
  font-weight: 500;
  color: #495057;
  margin-top: 0.25rem;
}

.remove-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 50%;
  font-size: 1.25rem;
  line-height: 1;
}

.no-colors {
  text-align: center;
  padding: 2rem;
  color: #6c757d;
}

.text-muted {
  opacity: 0.7;
  font-size: 0.875rem;
}

/* 导出导入功能样式 */
.export-import-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.export-import-section h4 {
  margin-bottom: 1.5rem;
  color: #495057;
  font-size: 1.1rem;
}

.export-section,
.import-section {
  margin-bottom: 1.5rem;
}

.export-section h5,
.import-section h5 {
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 1rem;
}

.section-desc {
  margin-bottom: 1rem;
  color: #6c757d;
  font-size: 0.875rem;
  line-height: 1.4;
}

.export-actions,
.import-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.export-result {
  margin-top: 1rem;
}

.export-result label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
  font-size: 0.875rem;
}

.export-string-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.export-string-input {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
  background: #fff;
  color: #495057;
}

.copy-btn {
  padding: 0.75rem;
  border: 1px solid #6c757d;
  background: #6c757d;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-btn:hover {
  background: #5a6268;
  border-color: #5a6268;
}

.import-input-wrapper {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.import-string-input {
  flex: 1;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
  background: #fff;
}

.import-string-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.import-result {
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
}

.import-result.success {
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.import-result.error {
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* 批量导入功能样式 */
.batch-import-section {
  margin-bottom: 1.5rem;
}

.batch-import-section h5 {
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: 1rem;
}

.batch-import-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.batch-import-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.batch-import-textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.875rem;
  background: #fff;
  resize: vertical;
  min-height: 80px;
}

.batch-import-textarea:focus {
  outline: none;
  border-color: #ffc107;
  box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.25);
}

.btn-warning {
  padding: 0.75rem 1.5rem;
  border: 1px solid #ffc107;
  background: #ffc107;
  color: #212529;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  align-self: flex-start;
}

.btn-warning:hover {
  background: #e0a800;
  border-color: #d39e00;
}

.btn-warning:disabled {
  background: #e9ecef;
  border-color: #ced4da;
  color: #6c757d;
  cursor: not-allowed;
}

/* 画风切换功能样式 */
.art-style-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
}

.section-title {
  margin: 0 0 1rem 0;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
}

.color-merge-title {
  margin: 0 0 1rem 0;
  color: #495057;
  font-size: 1.1rem;
}

.style-selector {
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.style-selector label {
  font-weight: 500;
  color: white;
  white-space: nowrap;
}

.style-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  font-size: 0.875rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  min-width: 300px;
  backdrop-filter: blur(10px);
  width: 100%;
}

.style-select option {
  background: #667eea;
  color: white;
}

.style-select:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
}

.apply-style-btn {
  padding: 0.5rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.apply-style-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.style-info {
  background: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.style-description {
  margin: 0.5rem 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
}

.algorithm-tag {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 0.5rem;
}

.edit-btn {
  padding: 0.5rem 0.75rem;
}

/* 编辑弹窗 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: #fff;
  border-radius: 8px;
  width: 520px;
  max-width: 92vw;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.modal-title {
  margin: 0;
  padding: 1rem 1.25rem 0 1.25rem;
  color: #343a40;
}

.modal-body {
  padding: 1rem 1.25rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0 1.25rem 1.25rem 1.25rem;
}

.edit-preview {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

/* 删除历史样式 */
.deleted-history {
  margin-top: 1rem;
  padding: 1rem;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.history-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.75rem;
  max-height: 240px;
  overflow-y: auto;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  border: 1px solid #eee;
  border-radius: 8px;
}

.history-item .color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.history-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}

/* 左侧悬浮 删除历史（桌面端） */
.deleted-floating {
  position: fixed;
  left: 16px;
  top: 120px;
  width: 300px;
  max-height: calc(100vh - 160px);
  overflow: auto;
  z-index: 1000;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  padding: 10px;
}

.deleted-floating-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.deleted-floating-header .count {
  color: #6c757d;
  font-size: 0.9rem;
}

.btn-link {
  background: none;
  border: none;
  color: #d9534f;
  cursor: pointer;
}

.deleted-floating-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.deleted-floating-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px;
  border: 1px solid #eee;
  border-radius: 8px;
}

.deleted-floating-item .color-swatch {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.deleted-floating-item .meta {
  flex: 1;
  min-width: 0;
}

.deleted-floating-item .meta .name {
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.deleted-floating-item .meta .hex {
  font-size: 0.8rem;
  color: #6c757d;
}

.btn-secondary.small {
  padding: 4px 8px;
  font-size: 12px;
}

/* 移动端：不悬浮，回到内联（由原有的“删除历史”块承担显示） */
@media (max-width: 1023px) {
  .deleted-floating {
    display: none;
  }
}
</style>
